<script setup lang="ts">
  import { computed } from 'vue';
  import { IconRadio, SelectInput, IconImage, ThemeProvider } from '@/shared/ui';
  import { sanitizeIntegerInput } from '@/shared/lib';
  import { useFilterManagementModal } from '../model';

  import ArrowRight from '@/shared/assets/icons/ArrowRight.svg';
  import AddButton from '@/shared/assets/icons/AddButton.svg';
  import DeleteButton from '@/shared/assets/icons/DeleteButton.svg';

  export interface HtmRow {
    local: string;
    copies: number;
  }

  const props = withDefaults(
    defineProps<{
      format: string;
      rows?: HtmRow[];
    }>(),
    {
      rows: () => [{ local: '', copies: 0 }],
    }
  );

  const emit = defineEmits<{
    (e: 'update:format', value: string): void;
    (e: 'update:rows', value: HtmRow[]): void;
  }>();

  const UPDATE_ROWS_EVENT = 'update:rows';

  const { physicalLocations, loadingLocations, locationsError } = useFilterManagementModal();

  const locationOptions = computed(() => {
    if (loadingLocations.value) {
      return [{ id: '__loading__', name: 'Carregando locais...', disabled: true }];
    }

    if (locationsError.value) {
      return [{ id: '__error__', name: 'Erro ao carregar locais', disabled: true }];
    }

    return physicalLocations.value.map((location) => ({
      id: location.id,
      name: location.name,
    }));
  });
  const MAX_ROWS = 20;

  const rowsLocationOptions = computed(() =>
    props.rows.map((_, index) => {
      const selectedInOtherRows = new Set(
        props.rows
          .filter((_, rowIndex) => rowIndex !== index)
          .map((row) => String(row.local))
          .filter(Boolean)
      );

      return locationOptions.value.filter((option) => !selectedInOtherRows.has(String(option.id)));
    })
  );

  const maxAddableRows = computed(() => {
    if (loadingLocations.value || locationsError.value) return MAX_ROWS;
    return Math.min(physicalLocations.value.length, MAX_ROWS);
  });

  const canAddRow = computed(() => props.rows.length < maxAddableRows.value);

  const addRow = () => {
    if (!canAddRow.value) return;
    emit(UPDATE_ROWS_EVENT, [...props.rows, { local: '', copies: 0 }]);
  };

  const removeRow = (index: number) => {
    const updated = props.rows.filter((_, i) => i !== index);
    emit(UPDATE_ROWS_EVENT, updated);
  };

  const updateRowLocal = (index: number, value: string) => {
    const updated = props.rows.map((row, i) => (i === index ? { ...row, local: value } : row));
    emit(UPDATE_ROWS_EVENT, updated);
  };

  const updateRowCopies = (index: number, value: number) => {
    const updated = props.rows.map((row, i) => (i === index ? { ...row, copies: value } : row));
    emit(UPDATE_ROWS_EVENT, updated);
  };

  const handleCopiesInput = (index: number, event: Event) => {
    const input = event.target as HTMLInputElement;
    const sanitized = sanitizeIntegerInput(input.value);
    input.value = String(sanitized);
    updateRowCopies(index, sanitized);
  };

  const incrementCopies = (index: number) => {
    const updated = props.rows.map((row, i) =>
      i === index ? { ...row, copies: row.copies + 1 } : row
    );
    emit(UPDATE_ROWS_EVENT, updated);
  };

  const decrementCopies = (index: number) => {
    const updated = props.rows.map((row, i) =>
      i === index ? { ...row, copies: Math.max(0, row.copies - 1) } : row
    );
    emit(UPDATE_ROWS_EVENT, updated);
  };
</script>

<template>
  <ThemeProvider>
    <div class="flex flex-col gap-3 p-6">
      <label class="text-sm font-semibold text-gray-07">Formato:</label>
      <div class="flex gap-6">
        <IconRadio
          :model-value="format"
          value="EL"
          label="Eletrônico"
          name="format"
          @update:model-value="emit('update:format', $event)"
        />
        <IconRadio
          :model-value="format"
          value="EL_FS"
          label="Eletrônico/Físico"
          name="format"
          @update:model-value="emit('update:format', $event)"
        />
      </div>
    </div>

    <div v-if="format === 'EL_FS'">
      <div class="flex justify-between items-start pr-6 border-t border-gray-02">
        <div class="w-full gap-4 p-6">
          <div
            v-for="(row, index) in rows"
            :key="index"
            class="flex flex-row gap-4 items-end justify-between"
          >
            <div class="flex flex-row gap-4 mb-2 items-end justify-between w-135">
              <div class="w-full">
                <label class="text-sm font-semibold text-gray-07">Local HTM:</label>
                <SelectInput
                  :model-value="row.local"
                  class="h-11"
                  :options="rowsLocationOptions[index]"
                  label="Selecione o local"
                  @update:model-value="updateRowLocal(index, $event)"
                />
              </div>
              <div class="w-full">
                <label class="text-sm font-semibold text-gray-07">Quantidade de cópias</label>
                <div class="relative w-25">
                  <input
                    :value="row.copies"
                    type="text"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    autocomplete="off"
                    class="w-25 px-4 h-11 rounded-lg border border-gray-02 text-gray-03 [appearance:textfield] [-webkit-appearance:textfield] [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    @input="handleCopiesInput(index, $event)"
                    min="0"
                  >
                  <div class="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col">
                    <IconImage
                      :src="ArrowRight"
                      alt="Aumentar"
                      class="h-5 w-5 rotate-[270deg] cursor-pointer translate-y-[3px] -ml-[0.8px]"
                      @click="incrementCopies(index)"
                    />
                    <IconImage
                      :src="ArrowRight"
                      alt="Diminuir"
                      class="h-5 w-5 rotate-[90deg] cursor-pointer -translate-y-[3px]"
                      @click="decrementCopies(index)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="h-11 flex items-center text-[var(--theme-secondary-ink)]">
              <IconImage
                v-if="index > 0"
                :src="DeleteButton"
                alt="Remover"
                class="cursor-pointer"
                @click="removeRow(index)"
              />
              <IconImage
                v-if="index === 0"
                :src="AddButton"
                alt="Adicionar"
                :class="canAddRow ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'"
                @click="addRow"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </ThemeProvider>
</template>
